import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AnimatedSection } from "@/components/AnimatedSection";
import SEO, { SITE_URL, breadcrumbs } from "@/components/SEO";
import { landingPages } from "@/lib/landingPages";

/**
 * Keyword landing pages (e.g. /ai-services, /ai-implementation).
 * Content lives in src/lib/landingPages.js so new pages need no new component.
 */
export default function SeoLandingPage({ slug }) {
  const page = landingPages[slug];
  const path = `/${slug}`;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: page.serviceName,
      serviceType: page.serviceName,
      description: page.description,
      url: `${SITE_URL}${path}`,
      provider: { "@id": `${SITE_URL}/#organization` },
      areaServed: [{ "@type": "Country", name: "India" }, "Worldwide"],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: page.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    breadcrumbs([
      { name: "Home", path: "/" },
      { name: page.breadcrumb, path },
    ]),
  ];

  return (
    <div className="overflow-hidden bg-white" data-testid={`landing-${slug}`}>
      <SEO title={page.title} description={page.description} path={path} jsonLd={jsonLd} />

      {/* Hero */}
      <section className="relative pt-36 pb-20 bg-gradient-to-br from-white via-slate-50 to-blue-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-5xl text-center">
          <span className="inline-block px-4 py-2 bg-[#243b7f]/10 text-[#243b7f] rounded-full text-sm font-medium mb-6">
            {page.eyebrow}
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#0f172a] font-['Manrope'] mb-6">
            {page.h1}
          </h1>
          <p className="text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto mb-10">{page.intro}</p>
          <Link to="/contact">
            <Button className="bg-[#243b7f] hover:bg-[#1a2d63] text-white rounded-full px-8 py-6 text-base">
              Talk to our AI team <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>

      {/* Offerings */}
      <section className="py-20">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl">
          <AnimatedSection>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] font-['Manrope'] mb-4">{page.offeringsTitle}</h2>
            <p className="text-lg text-slate-600 mb-12 max-w-3xl">{page.offeringsIntro}</p>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {page.offerings.map((o) => (
              <div key={o.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="text-xl font-semibold text-[#0f172a] mb-3">{o.title}</h3>
                <p className="text-slate-600 leading-relaxed">{o.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] font-['Manrope'] mb-12">{page.processTitle}</h2>
          <ol className="grid md:grid-cols-2 gap-8">
            {page.process.map((s, i) => (
              <li key={s.title} className="flex gap-4">
                <span className="flex-none w-10 h-10 rounded-full bg-[#243b7f] text-white flex items-center justify-center font-bold">
                  {i + 1}
                </span>
                <div>
                  <h3 className="text-lg font-semibold text-[#0f172a] mb-1">{s.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{s.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Why VANCO */}
      <section className="py-20">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] font-['Manrope'] mb-6">Why enterprises choose VANCO</h2>
            <ul className="space-y-4">
              {page.why.map((w) => (
                <li key={w} className="flex gap-3 text-slate-700 leading-relaxed">
                  <CheckCircle className="flex-none text-[#243b7f] mt-1" size={20} />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] font-['Manrope'] mb-6">Related case studies</h2>
            <ul className="space-y-3">
              {page.caseStudies.map((c) => (
                <li key={c.id}>
                  <Link to={`/case-studies/${c.id}`} className="group flex items-center justify-between rounded-xl border border-slate-200 p-4 hover:border-[#243b7f] transition-colors">
                    <span className="font-medium text-[#0f172a]">{c.title}</span>
                    <ArrowRight className="text-[#243b7f] group-hover:translate-x-1 transition-transform" size={18} />
                  </Link>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-slate-600">
              See also: {page.related.map((r, i) => (
                <span key={r.path}>
                  {i > 0 && " · "}
                  <Link to={r.path} className="text-[#243b7f] underline underline-offset-2">{r.name}</Link>
                </span>
              ))}
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] font-['Manrope'] mb-10">Frequently asked questions</h2>
          <div className="space-y-4">
            {page.faqs.map((f) => (
              <details key={f.q} className="rounded-xl bg-white border border-slate-200 p-5 open:shadow-sm">
                <summary className="cursor-pointer font-semibold text-[#0f172a]">{f.q}</summary>
                <p className="mt-3 text-slate-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0f172a] text-white text-center">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] mb-4">{page.ctaTitle}</h2>
          <p className="text-slate-300 text-lg mb-8">{page.ctaText}</p>
          <Link to="/contact">
            <Button className="bg-[#f7c550] hover:bg-[#e8b53f] text-[#0f172a] rounded-full px-8 py-6 text-base font-semibold">
              Book a consultation <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
